import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Tabs, Tab, Row, Col, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { employeeSignMyPaf, getMyPafById, resetMyPafRecord } from "actions";
import { F } from "pages/AppraisalManagment/constants/legacyApiKeys";
import {
  buildSectionCategoriesFromRecord,
  canEmployeeEditPaf,
  canEmployeeSignPaf,
  isEmployeePafSigned,
  mapMyPafRecordToEmployeeFormState,
} from "pages/AppraisalManagment/utils/myPafHelpers";
import {
  calculateAppraisalSummaryPercentage,
  formatSummaryPercentageDisplay,
  mergeScoringRowWithSectionData,
} from "pages/AppraisalManagment/utils/appraisalProgressSummary";
import AppraisalProgressFormHeader from "../../Appraisal/AppraisalProgressForm/component/AppraisalProgressFormHeader/AppraisalProgressFormHeader";
import {
  AppraisalCommentTab,
  DevelopmentPlanTab,
  SectionCategoryTab,
  SignOffTab,
} from "../../Appraisal/AppraisalProgressForm/component";
import "../../Appraisal/AppraisalProgress.css";

const EMPLOYEE_SIGNATURE_TAB_KEY = "employee-signature";

const EmployeePafSign = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const appraisalState = useSelector((state) => state.appraisalState);

  const [activeKey, setActiveKey] = useState("");
  const [sectionCategories, setSectionCategories] = useState({});
  const [selectedSectionTabs, setSelectedSectionTabs] = useState([]);
  const [formState, setFormState] = useState(() => mapMyPafRecordToEmployeeFormState(null));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const didSetInitialTab = useRef(false);

  const recordId = routeId || null;
  const record = appraisalState.myPafRecord || null;
  const alreadySigned = isEmployeePafSigned(record);
  const canSignPaf = canEmployeeSignPaf(record);
  const canOpenSignScreen = canEmployeeEditPaf(record);
  const signOffReadOnly = alreadySigned || !canSignPaf;

  useEffect(() => {
    if (recordId) {
      dispatch(getMyPafById(recordId));
    }
    return () => {
      dispatch(resetMyPafRecord());
    };
  }, [dispatch, recordId]);

  useEffect(() => {
    if (!record) return;
    setFormState(mapMyPafRecordToEmployeeFormState(record));
    setSectionCategories(buildSectionCategoriesFromRecord(record));
  }, [record]);

  const hardcodedTabs = useMemo(
    () => [
      { label: "Appraiser's Comments​", value: "AppraisersComments​", type: "hardcoded" },
      { label: "Developmental Plan​", value: "DevelopmentalPlan​", type: "hardcoded" },
      { label: "Employee Signature", value: EMPLOYEE_SIGNATURE_TAB_KEY, type: "hardcoded" },
    ],
    []
  );

  useEffect(() => {
    const categoryTabs = Object.keys(sectionCategories).map((categoryTitle) => ({
      label: categoryTitle,
      value: `category-${categoryTitle}`,
      type: "category",
    }));
    setSelectedSectionTabs(categoryTabs);

    if (!didSetInitialTab.current) {
      const signatureTabAvailable = record && canOpenSignScreen;

      if (signatureTabAvailable) {
        setActiveKey(EMPLOYEE_SIGNATURE_TAB_KEY);
        didSetInitialTab.current = true;
        return;
      }

      if (categoryTabs.length > 0) {
        setActiveKey(categoryTabs[0].value);
        didSetInitialTab.current = true;
      } else if (!activeKey) {
        setActiveKey(EMPLOYEE_SIGNATURE_TAB_KEY);
        didSetInitialTab.current = true;
      }
      return;
    }

    if (categoryTabs.length > 0 && activeKey && !categoryTabs.some((tab) => tab.value === activeKey)) {
      const stillValid = [...categoryTabs, ...hardcodedTabs].some((tab) => tab.value === activeKey);
      if (!stillValid) {
        setActiveKey(categoryTabs[0].value);
      }
    }
  }, [sectionCategories, record, hardcodedTabs, activeKey]);

  const allTabs = useMemo(
    () => [...selectedSectionTabs, ...hardcodedTabs],
    [selectedSectionTabs, hardcodedTabs]
  );

  const sectionWeightageByTitle = useMemo(() => {
    const weights = {};
    (record?.sections || record?.[F.listAlternatePlural] || []).forEach((sectionRow) => {
      const titleKey = String(sectionRow?.title ?? "").trim().toLowerCase();
      const weight = Number(sectionRow?.weightage);
      if (titleKey && Number.isFinite(weight) && weight > 0) {
        weights[titleKey] = weight;
      }
    });
    return weights;
  }, [record]);

  const liveSummaryPercentage = useMemo(
    () =>
      calculateAppraisalSummaryPercentage({
        sectionCategories,
        sectionData: formState.sectionData,
        sectionWeightageByTitle,
      }),
    [sectionCategories, formState.sectionData, sectionWeightageByTitle]
  );

  const liveSummaryDisplay = formatSummaryPercentageDisplay(liveSummaryPercentage);

  const handleCancel = useCallback(() => {
    navigate("/my-pafs");
  }, [navigate]);

  const updateFormField = useCallback((field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const goToNextTab = useCallback(
    (currentTabKey) => {
      const idx = allTabs.findIndex((t) => t.value === currentTabKey);
      if (idx < 0 || idx >= allTabs.length - 1) return;
      setActiveKey(allTabs[idx + 1].value);
    },
    [allTabs]
  );

  const handleEmployeeSignatureSubmit = useCallback(
    async (signatureImage) => {
      if (!recordId || isSubmitting || alreadySigned || !canSignPaf) return;
      setIsSubmitting(true);
      try {
        await dispatch(
          employeeSignMyPaf(recordId, {
            employee_signature: signatureImage || formState.signatureImage || null,
          })
        );
        navigate("/my-pafs");
      } catch (error) {
        console.error("Employee PAF sign failed:", error);
        setIsSubmitting(false);
      }
    },
    [
      dispatch,
      navigate,
      recordId,
      isSubmitting,
      alreadySigned,
      canSignPaf,
      formState.signatureImage,
    ]
  );

  const renderTabContent = (tabKey) => {
    const tabIndex = allTabs.findIndex((t) => t.value === tabKey);
    const hasNextTab = tabIndex >= 0 && tabIndex < allTabs.length - 1;
    const nextTabControl = {
      onNext: hasNextTab ? () => goToNextTab(tabKey) : undefined,
      hasNextTab,
    };

    if (tabKey && tabKey.startsWith("category-")) {
      const categoryTitle = tabKey.replace("category-", "");
      const categoryRows = sectionCategories[categoryTitle] || [];
      const rowsWithMergedState = categoryRows.map((row) => {
        const merged = mergeScoringRowWithSectionData(
          row,
          categoryTitle,
          formState.sectionData
        );
        return {
          ...merged,
          description: merged.description ?? row.description,
          comments: merged.comments ?? row.comments ?? "",
        };
      });

      return (
        <SectionCategoryTab
          categoryName={categoryTitle}
          sections={rowsWithMergedState}
          onNext={nextTabControl.onNext}
          hasNextTab={nextTabControl.hasNextTab}
          onCancel={handleCancel}
          onSectionChange={() => {}}
          isReadOnly
        />
      );
    }

    if (tabKey === "AppraisersComments​") {
      return (
        <AppraisalCommentTab
          formState={formState}
          updateFormField={updateFormField}
          onNext={nextTabControl.onNext}
          hasNextTab={nextTabControl.hasNextTab}
          onCancel={handleCancel}
          isReadOnly
        />
      );
    }

    if (tabKey === "DevelopmentalPlan​") {
      return (
        <DevelopmentPlanTab
          formState={formState}
          updateFormField={updateFormField}
          onNext={nextTabControl.onNext}
          hasNextTab={nextTabControl.hasNextTab}
          onCancel={handleCancel}
          isReadOnly
        />
      );
    }

    if (tabKey === EMPLOYEE_SIGNATURE_TAB_KEY) {
      return (
        <>
          {!alreadySigned && !canSignPaf ? (
            <div className="alert alert-warning mb-3 py-2 font-13">
              Employee Signature is not available for this PAF.
            </div>
          ) : null}
          {canSignPaf || alreadySigned ? (
            <SignOffTab
              formState={formState}
              updateFormField={updateFormField}
              onCancel={handleCancel}
              onApprove={handleEmployeeSignatureSubmit}
              isReadOnly={signOffReadOnly || isSubmitting}
              currentUserRole="employee"
              employeeSignMode
            />
          ) : null}
        </>
      );
    }

    return null;
  };

  if (!recordId) {
    return (
      <>
        <AppraisalProgressFormHeader />
        <div className="main-content-div p-3">
          <p className="text-muted mb-3">PAF not found.</p>
          <button type="button" className="c-b-btn" onClick={handleCancel}>
            Back to My PAFs
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <AppraisalProgressFormHeader />
      <div className="appraisal-progress-form">
        <div className="p-3">
          <Row>
            <Col lg={3}>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Appraisal Form Name</Form.Label>
                <input
                  type="text"
                  className="form-control input-field w-100"
                  value={formState.appraisal_name || ""}
                  readOnly
                  disabled
                />
              </Form.Group>
            </Col>
            <Col lg={3}>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Appraiser Name</Form.Label>
                <input
                  type="text"
                  className="form-control input-field w-100"
                  value={formState.appraiserName || ""}
                  readOnly
                  disabled
                />
              </Form.Group>
            </Col>
            <Col lg={3}>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Employee Name</Form.Label>
                <input
                  type="text"
                  className="form-control input-field w-100"
                  value={formState.employeeName || ""}
                  readOnly
                  disabled
                />
              </Form.Group>
            </Col>
            <Col lg={3}>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Date</Form.Label>
                <DatePicker
                  selected={formState.appraisalDateTime}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="yyyy-MM-dd HH:mm"
                  timeCaption="Time"
                  className="form-control input-field w-100"
                  placeholderText="Date"
                  disabled
                  isClearable={false}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>

        <div className="px-3 pb-2">
          <div className="alert alert-info mb-2 py-2">
            Read-only: fields are locked for role employee.
          </div>
        </div>

        <div className="appraisal-progress-form-tabs">
          {selectedSectionTabs.length > 0 && (
            <div className="appraisal-progress-summary" aria-live="polite">
              Summary:{" "}
              <strong>
                {liveSummaryDisplay != null ? `${liveSummaryDisplay}%` : "—"}
              </strong>
            </div>
          )}
          {allTabs.length > 0 ? (
            <Tabs activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
              {allTabs.map((tab) => (
                <Tab eventKey={tab.value} title={tab.label} key={tab.value}>
                  {renderTabContent(tab.value)}
                </Tab>
              ))}
            </Tabs>
          ) : (
            <p className="p-3 text-muted">No section data available for this PAF.</p>
          )}
        </div>
      </div>
    </>
  );
};

export default EmployeePafSign;
